import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allProjects } from '../data/projects';
import { EFFECTS } from '../data/effects';
import { getAllBlogs } from '../utils/blogLoader';
import { useSectionNavigation } from '../hooks/useSectionNavigation';

// Matches the cap enforced by ChatInput and the backend validators.
const MAX_TARS_QUERY_LENGTH = 1000;

interface Command {
  id: string;
  label: string;
  hint: string;
  keywords: string;
  run: () => void;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  setActiveSection: (section: string) => void;
  onAskTars: (query: string) => void;
  onTriggerEffect: (effectId: string, origin?: { x: number; y: number }) => void;
  lensEnabled: boolean;
  snitchActive: boolean;
}

const SECTIONS = [
  { id: 'projects', label: 'Projects' },
  { id: 'experience', label: 'Experience' },
  { id: 'contact', label: 'Contact' },
  { id: 'funhouse', label: 'Funhouse' },
];

// Hidden easter eggs — deliberately excluded from the default (empty-query)
// list so they stay something you have to know or stumble onto, not
// something Cmd+K advertises up front. Metadata itself (id/label/keywords)
// lives in ../data/effects, shared with the openly-listed Funhouse section —
// this palette only adds the "hidden unless searched for" behavior below.
const EFFECT_COMMANDS = EFFECTS;

const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  setActiveSection,
  onAskTars,
  onTriggerEffect,
  lensEnabled,
  snitchActive,
}) => {
  const navigate = useNavigate();
  const navigateToSection = useSectionNavigation(setActiveSection);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const commands = useMemo<Command[]>(() => {
    const sectionCommands = SECTIONS.map((section) => ({
      id: `section-${section.id}`,
      label: section.label,
      hint: 'section',
      keywords: section.label,
      run: () => navigateToSection(section.id),
    }));

    const projectCommands = allProjects.map((project) => ({
      id: `project-${project.id}`,
      label: project.title,
      hint: 'project',
      keywords: `${project.title} ${project.tags.join(' ')}`,
      run: () => {
        navigate('/projects');
        // Wait for the projects route to render before scrolling to the card.
        setTimeout(() => {
          document
            .getElementById(`project-${project.id}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      },
    }));

    const blogCommands = getAllBlogs().map((blog) => ({
      id: `blog-${blog.id}`,
      label: blog.title,
      hint: 'blog',
      keywords: `blog ${blog.title} ${blog.tags.join(' ')}`,
      run: () => navigate(`/blogs/${blog.id}`),
    }));

    return [
      ...sectionCommands,
      {
        id: 'blogs-index',
        label: 'All blog posts',
        hint: 'blog',
        keywords: 'blog blogs posts writing',
        run: () => navigate('/blogs'),
      },
      ...projectCommands,
      ...blogCommands,
    ];
  }, [navigate, navigateToSection]);

  const effectCommands = useMemo<Command[]>(
    () =>
      EFFECT_COMMANDS.map((effect) => {
        // The lens and the Snitch are toggles, not one-shot triggers like
        // the others, so their label reflects current state — re-opening
        // the palette and searching shows you what selecting it will do.
        let label = effect.label;
        if (effect.id === 'gravity-lens') label = `Gravity Lens: ${lensEnabled ? 'On' : 'Off'}`;
        else if (effect.id === 'golden-snitch' && snitchActive) label = 'Recall the Snitch';
        return {
          id: `effect-${effect.id}`,
          label,
          hint: 'effect',
          keywords: effect.keywords,
          // The Snitch spawns out of the palette input itself, so its
          // trigger needs the input's on-screen position — read
          // synchronously here, before the palette's own onClose() unmounts
          // it. Harmless to compute even when this selection will actually
          // disarm rather than spawn — App.tsx ignores origin in that case.
          run: () => {
            if (effect.id === 'golden-snitch') {
              const rect = inputRef.current?.getBoundingClientRect();
              const origin = rect
                ? { x: rect.left + Math.min(120, rect.width * 0.4), y: rect.top + rect.height / 2 }
                : undefined;
              onTriggerEffect(effect.id, origin);
            } else {
              onTriggerEffect(effect.id);
            }
          },
        };
      }),
    [onTriggerEffect, lensEnabled, snitchActive]
  );

  const trimmedQuery = query.trim();

  const results = useMemo(() => {
    if (!trimmedQuery) {
      return commands;
    }
    const needle = trimmedQuery.toLowerCase();
    return [...commands, ...effectCommands].filter((command) =>
      command.keywords.toLowerCase().includes(needle)
    );
  }, [commands, effectCommands, trimmedQuery]);

  // Falls through to TARS when nothing matches, so a question is never a dead end.
  const askTarsCommand = useMemo<Command | null>(() => {
    if (!trimmedQuery) {
      return null;
    }
    return {
      id: 'ask-tars',
      label: `Ask TARS: "${trimmedQuery}"`,
      hint: 'chat',
      keywords: '',
      run: () => onAskTars(trimmedQuery.slice(0, MAX_TARS_QUERY_LENGTH)),
    };
  }, [onAskTars, trimmedQuery]);

  const items = useMemo(
    () => (askTarsCommand ? [...results, askTarsCommand] : results),
    [askTarsCommand, results]
  );

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [trimmedQuery]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) {
    return null;
  }

  const runItem = (item: Command) => {
    onClose();
    item.run();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Stop here so the chat widget's own Escape handler doesn't also fire.
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (items.length ? (i + 1) % items.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[selectedIndex];
      if (item) {
        runItem(item);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 pt-[15vh]"
      onClick={onClose}
      data-lens-exclude
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-full max-w-xl bg-dev-bg-card border border-dev-border rounded-xl shadow-dev-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Mirrors the sidebar's terminal prompt so the palette reads as that bar expanding */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-dev-border font-mono text-sm">
          <span className="text-dev-accent-cyan">&gt;</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a section, project or post — or ask TARS"
            aria-label="Search commands"
            aria-controls="command-palette-list"
            aria-activedescendant={items[selectedIndex]?.id}
            className="flex-1 bg-transparent outline-none text-base sm:text-sm text-dev-text placeholder:text-dev-text-dimmed"
            maxLength={MAX_TARS_QUERY_LENGTH}
          />
        </div>

        <ul
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          className="max-h-80 overflow-y-auto py-2"
        >
          {items.map((item, index) => (
            <li
              key={item.id}
              id={item.id}
              data-index={index}
              role="option"
              aria-selected={index === selectedIndex}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => runItem(item)}
              className={`flex items-center justify-between gap-3 px-4 py-2 font-mono text-sm cursor-pointer ${
                index === selectedIndex
                  ? 'bg-dev-bg-card-hover text-dev-text'
                  : 'text-dev-text-muted'
              }`}
            >
              <span className="truncate">{item.label}</span>
              <span className="text-xs text-dev-text-dimmed flex-shrink-0">{item.hint}</span>
            </li>
          ))}
          {items.length === 0 && (
            <li className="px-4 py-2 font-mono text-sm text-dev-text-dimmed">No matches</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CommandPalette;
